import { useState } from "react";
import { Box, Grid2, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { Add, PlaylistAdd, Desk, LocalParking, EvStation } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { PlaceSmallIcon, PlaceMediumIcon } from "../../Canvas/Partials/place-icons.component";

interface Props {
  isMultiPlaces: boolean;
  setIsMultiPlaces: (m: boolean) => void;
  setNumberOfPlacesAdd: (n: number) => void;
  typeOfPlaces: number;
  setTypeOfPlaces: (t: number) => void;
}

export function PlaceSideToolbar({
  isMultiPlaces,
  setIsMultiPlaces,
  setNumberOfPlacesAdd,
  typeOfPlaces,
  setTypeOfPlaces
}: Readonly<Props>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const iconFilledWithTheme = theme.palette.mode === "dark" ? "#ffffff" : "#000000";
  const iconStrokeWithTheme = theme.palette.mode === "dark" ? "#2b2b2b" : "#DBDBDB";
  const placeIconBoxClass = {
    width: 160,
    margin: "5px 0",
    border: theme.palette.mode === "dark" ? "1px solid #ffffff" : "1px solid #000000",
    textAlign: "center",
    cursor: "pointer"
  };
  const placeItemClass = { paddingTop: "5px" };
  const boxBgColor = theme.palette.mode === "dark" ? "#000000" : "#ffffff";
  const placeBoxStyle = {
    border: "1px solid #00000088",
    backgroundColor: boxBgColor,
    opacity: 0.95,
    borderRadius: 2
  };

  const [placeNumberBoxOpen, setPlaceNumberBoxOpen] = useState(false);

  return (
    <Grid2
      container
      data-testid="place-side-toolbar-container"
      sx={{
        position: "absolute",
        top: "176px",
        left: "8px",
        flexDirection: "column",
        alignContent: "flex-start",
        width: "auto"
      }}
    >
      {/* select place types */}
      <Grid2 data-testid="place-side-toolbar-type-item" sx={{ width: "fit-content" }}>
        <Box sx={{ ...placeBoxStyle }}>
          <Grid2 container sx={{ width: 100 }}>
            {/** create place type 1 desk */}
            <Grid2
              size={{ xs: 6 }}
              sx={[
                { borderRadius: 2 },
                typeOfPlaces === 1 ? { backgroundColor: "blue" } : { backgroundColor: null }
              ]}
            >
              <Tooltip title={<Typography>{t("Workplace")}</Typography>} aria-label={"Workplace"}>
                <IconButton
                  data-testid="btn-select-plType-1"
                  onClick={() => {
                    setTypeOfPlaces(1);
                    setPlaceNumberBoxOpen(true);
                  }}
                  size={"large"}
                >
                  <Desk sx={[typeOfPlaces === 1 ? { color: "white" } : { color: null }]} />
                </IconButton>
              </Tooltip>
            </Grid2>

            {/** create place type 2 park place */}
            <Grid2
              size={{ xs: 6 }}
              sx={[
                { borderRadius: 2 },
                typeOfPlaces === 2 ? { backgroundColor: "blue" } : { backgroundColor: null }
              ]}
            >
              <Tooltip title={<Typography>{t("Parking")}</Typography>} aria-label={"Parking"}>
                <IconButton
                  data-testid="btn-select-plType-2"
                  onClick={() => {
                    setTypeOfPlaces(2);
                    setPlaceNumberBoxOpen(true);
                  }}
                  size={"large"}
                >
                  <LocalParking sx={[typeOfPlaces === 2 ? { color: "white" } : { color: null }]} />
                </IconButton>
              </Tooltip>
            </Grid2>

            {/** create place type 4 electric charging station */}
            <Grid2
              size={{ xs: 6 }}
              sx={[
                { borderRadius: 2 },
                typeOfPlaces === 4 ? { backgroundColor: "blue" } : { backgroundColor: null }
              ]}
            >
              <Tooltip
                title={<Typography>{t("Electric Charging")}</Typography>}
                aria-label={"Electric Charging"}
              >
                <IconButton
                  data-testid="btn-select-plType-4"
                  onClick={() => {
                    setTypeOfPlaces(4);
                    setPlaceNumberBoxOpen(true);
                  }}
                  size={"large"}
                >
                  <EvStation sx={[typeOfPlaces === 4 ? { color: "white" } : { color: null }]} />
                </IconButton>
              </Tooltip>
            </Grid2>
          </Grid2>
        </Box>
      </Grid2>
      {/* select single place or multi places */}
      <Grid2 data-testid="place-side-toolbar-number-item" sx={{ width: "fit-content" }}>
        {placeNumberBoxOpen && (
          <Box sx={{ ...placeBoxStyle, width: "fit-content" }}>
            <Grid2 container sx={{ width: 100 }}>
              {/** create new single place */}
              <Grid2
                size={{ xs: 6 }}
                sx={[
                  { borderRadius: 2 },
                  isMultiPlaces ? { backgroundColor: null } : { backgroundColor: "blue" }
                ]}
              >
                <Tooltip
                  title={<Typography>{t("SinglePlaceSideToolbarLabel")}</Typography>}
                  aria-label={"SinglePlaceSideToolbarLabel"}
                >
                  <IconButton
                    data-testid="btn-create-single-pl"
                    onClick={() => {
                      setIsMultiPlaces(false);
                      setNumberOfPlacesAdd(1);
                    }}
                    size={"large"}
                  >
                    <Add sx={[isMultiPlaces ? { color: null } : { color: "white" }]} />
                  </IconButton>
                </Tooltip>
              </Grid2>
              {/** create new multi places */}
              <Grid2
                size={{ xs: 6 }}
                sx={[
                  { borderRadius: 2 },
                  isMultiPlaces ? { backgroundColor: "blue" } : { backgroundColor: null }
                ]}
              >
                <Tooltip
                  title={<Typography>{t("MultiPlacesSideToolbarLabel")}</Typography>}
                  aria-label={"MultiPlacesSideToolbarLabel"}
                >
                  <IconButton
                    data-testid="btn-create-multi-pl"
                    onClick={() => {
                      setIsMultiPlaces(true);
                      setNumberOfPlacesAdd(2);
                    }}
                    size={"large"}
                  >
                    <PlaylistAdd sx={[isMultiPlaces ? { color: "white" } : { color: null }]} />
                  </IconButton>
                </Tooltip>
              </Grid2>
            </Grid2>
          </Box>
        )}
        {isMultiPlaces && (
          <Box sx={{ ...placeBoxStyle }}>
            <Grid2
              size={{ xs: 12 }}
              id="place-icon-container"
              container
              sx={{ width: 190, padding: "5px", placeContent: "center" }}
            >
              <Grid2
                id="place-icon-box-2"
                sx={placeIconBoxClass}
                data-testid="place-icon-box-2"
                onClick={() => setNumberOfPlacesAdd(2)}
              >
                <Grid2 id="place-icon-item-2" sx={placeItemClass}>
                  <PlaceMediumIcon fill={iconFilledWithTheme} stroke={iconStrokeWithTheme} />
                  <PlaceMediumIcon fill={iconFilledWithTheme} stroke={iconStrokeWithTheme} />
                </Grid2>
              </Grid2>
              <Grid2
                id="place-icon-box-3"
                sx={placeIconBoxClass}
                data-testid="place-icon-box-3"
                onClick={() => setNumberOfPlacesAdd(3)}
              >
                <Grid2 id="place-icon-item-3" sx={placeItemClass}>
                  <PlaceMediumIcon fill={iconFilledWithTheme} stroke={iconStrokeWithTheme} />
                  <PlaceMediumIcon fill={iconFilledWithTheme} stroke={iconStrokeWithTheme} />
                  <PlaceMediumIcon fill={iconFilledWithTheme} stroke={iconStrokeWithTheme} />
                </Grid2>
              </Grid2>
              <Grid2
                id="place-icon-box-4"
                sx={placeIconBoxClass}
                data-testid="place-icon-box-4"
                onClick={() => setNumberOfPlacesAdd(4)}
              >
                <Grid2
                  id="place-icon-item-4"
                  size={{ xs: 6 }}
                  sx={{ ...placeItemClass, margin: "auto" }}
                >
                  <PlaceSmallIcon fill={iconFilledWithTheme} stroke={iconStrokeWithTheme} />
                  <PlaceSmallIcon fill={iconFilledWithTheme} stroke={iconStrokeWithTheme} />
                  <PlaceSmallIcon fill={iconFilledWithTheme} stroke={iconStrokeWithTheme} />
                  <PlaceSmallIcon fill={iconFilledWithTheme} stroke={iconStrokeWithTheme} />
                </Grid2>
              </Grid2>

              <Grid2
                id="place-icon-box-6"
                sx={placeIconBoxClass}
                data-testid="place-icon-box-6"
                onClick={() => setNumberOfPlacesAdd(6)}
              >
                <Grid2
                  id="place-icon-item-6"
                  size={{ xs: 8 }}
                  sx={{ ...placeItemClass, margin: "auto" }}
                >
                  <PlaceSmallIcon fill={iconFilledWithTheme} stroke={iconStrokeWithTheme} />
                  <PlaceSmallIcon fill={iconFilledWithTheme} stroke={iconStrokeWithTheme} />
                  <PlaceSmallIcon fill={iconFilledWithTheme} stroke={iconStrokeWithTheme} />
                  <PlaceSmallIcon fill={iconFilledWithTheme} stroke={iconStrokeWithTheme} />
                  <PlaceSmallIcon fill={iconFilledWithTheme} stroke={iconStrokeWithTheme} />
                  <PlaceSmallIcon fill={iconFilledWithTheme} stroke={iconStrokeWithTheme} />
                </Grid2>
              </Grid2>
            </Grid2>
          </Box>
        )}
      </Grid2>
    </Grid2>
  );
}
